import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  Loader2, CheckCircle2, Crown, Bot, Headphones, Sparkles, ArrowRight, AlertCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { trackPurchase, trackPremiumUnlock } from "../lib/analytics";

import { API } from "../lib/api";
// API now resolved at runtime via /lib/api.js
/**
 * /payment-success — Cinematic post-payment page.
 * 1) reads ?session_id from URL
 * 2) polls /api/payments/status/{id} until paid (or fails)
 * 3) refreshes user (is_pro flips to true)
 * 4) fires confetti and shows next-step CTAs
 */
export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { token, refreshUser, user } = useAuth();
  const { t, lang } = useLanguage();
  const p = t.pro;
  const a = t.auth;
  const navigate = useNavigate();
  const [state, setState] = useState("polling"); // polling | success | error
  const firedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    if (!token) return; // wait for auth to hydrate

    let attempts = 0;
    const MAX = 18; // ~36s @ 2s
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.clone().json();
          if (data.payment_status === "paid") {
            await refreshUser();
            setState("success");
            // GA4 — conversion: purchase + premium unlock
            try {
              trackPurchase({
                transactionId: sessionId,
                value: 39.90,
                plan: "pro_lifetime",
              });
              trackPremiumUnlock();
            } catch (e) {
              // GA4 analytics failures must NOT break the purchase confirmation flow,
              // but we log them so we can spot a missing conversion attribution.
              // eslint-disable-next-line no-console
              console.warn("[payment] analytics tracking failed", e);
            }
            fireConfetti();
            return;
          }
          if (data.status === "expired") {
            setState("error"); return;
          }
        }
      } catch (e) {
        // Network/poll failures are retried automatically up to MAX attempts.
        // Log so we can investigate persistent retry loops.
        // eslint-disable-next-line no-console
        console.warn("[payment] status poll failed (retrying)", e);
      }
      if (attempts >= MAX) { setState("error"); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, token, refreshUser]);

  const fireConfetti = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    const colors = ["#ff8c00", "#f59e0b", "#00e5ff", "#a855f7", "#10b981"];
    const duration = 2200;
    const end = Date.now() + duration;
    const burst = () => {
      confetti({
        particleCount: 4, angle: 60, spread: 65,
        origin: { x: 0, y: 0.6 }, colors, scalar: 0.9,
        ticks: 220,
      });
      confetti({
        particleCount: 4, angle: 120, spread: 65,
        origin: { x: 1, y: 0.6 }, colors, scalar: 0.9,
        ticks: 220,
      });
      if (Date.now() < end) requestAnimationFrame(burst);
    };
    // Big central burst
    confetti({
      particleCount: 130, spread: 120, origin: { y: 0.55 },
      colors, scalar: 1.1, ticks: 260, startVelocity: 42,
    });
    burst();
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-black text-white antialiased">
      {/* Cinematic backdrop */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-[480px] w-[480px] rounded-full bg-orange-500/15 blur-[120px]" />
        <div className="absolute -right-32 top-1/3 h-[420px] w-[420px] rounded-full bg-fuchsia-500/15 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 h-[420px] w-[420px] rounded-full bg-cyan-400/10 blur-[120px]" />
        {/* grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-4xl flex-col items-center justify-center px-6 py-16 text-center">
        <AnimatePresence mode="wait">
          {state === "polling" && (
            <motion.div
              key="polling"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="payment-success-polling"
            >
              <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-400/5 shadow-[0_0_60px_rgba(0,229,255,0.25)]">
                <Loader2 className="h-8 w-8 animate-spin text-cyan-300" />
              </div>
              <h1 className="font-display text-3xl font-black tracking-tighter text-white md:text-5xl">
                {p.processing}
              </h1>
              <p className="mt-3 text-base text-white/55">
                {lang === "es" && "Estamos confirmando tu pago con Stripe. No cierres esta ventana."}
                {lang === "en" && "We're confirming your payment with Stripe. Please don't close this window."}
                {lang === "sk" && "Potvrdzujeme tvoju platbu so Stripe. Nezatváraj toto okno."}
              </p>
            </motion.div>
          )}

          {state === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="payment-success-page"
            >
              {/* Crown badge */}
              <motion.div
                initial={{ rotate: -8, scale: 0.7 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ delay: 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-gradient-to-r from-amber-400/15 to-orange-500/15 px-4 py-2 shadow-[0_0_36px_rgba(255,180,0,0.35)]"
              >
                <Crown className="h-4 w-4 text-amber-300" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-200">
                  RE₿EL PRO · LIFETIME
                </span>
              </motion.div>

              <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tighter text-white md:text-6xl">
                {p.success}
              </h1>
              <p className="mx-auto mt-4 max-w-xl text-base text-white/65 md:text-lg">
                {lang === "es" && "Tu pago se ha confirmado. Ya tienes acceso ilimitado al Mentor IA y a la voz Onyx en las 51 lecciones."}
                {lang === "en" && "Your payment is confirmed. You now have unlimited access to the AI Mentor and the Onyx voice across all 51 lessons."}
                {lang === "sk" && "Tvoja platba je potvrdená. Máš teraz neobmedzený prístup k AI Mentorovi a hlasu Onyx vo všetkých 51 lekciách."}
              </p>

              {/* Features unlocked */}
              <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { Icon: Headphones, txt: lang === "en" ? "Onyx voice unlocked" : lang === "sk" ? "Hlas Onyx odomknutý" : "Voz Onyx desbloqueada" },
                  { Icon: Bot, txt: lang === "en" ? "AI Mentor unlocked" : lang === "sk" ? "AI Mentor odomknutý" : "Mentor IA desbloqueado" },
                ].map(({ Icon, txt }, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 + i * 0.08 }}
                    className="flex items-center justify-center gap-2 rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.06] px-4 py-3 text-sm text-emerald-200 backdrop-blur"
                  >
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    <Icon className="h-4 w-4" />
                    <span className="font-bold">{txt}</span>
                  </motion.div>
                ))}
              </div>

              {/* CTAs */}
              <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  onClick={() => navigate("/academy")}
                  data-testid="payment-success-cta-mentor"
                  className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-7 py-3.5 text-xs font-black uppercase tracking-[0.25em] text-black shadow-[0_0_30px_rgba(255,140,0,0.5)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_50px_rgba(255,140,0,0.7)]"
                >
                  <Sparkles className="h-4 w-4" />
                  {lang === "en" ? "Start using AI Mentor" : lang === "sk" ? "Začni s AI Mentorom" : "Empezar a usar Mentor IA"}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white/70 transition-all hover:border-white/35 hover:bg-white/10 hover:text-white"
                >
                  {lang === "en" ? "Back home" : lang === "sk" ? "Späť domov" : "Volver al inicio"}
                </Link>
              </div>

              {user?.email && (
                <p className="mt-8 text-[10px] uppercase tracking-[0.25em] text-white/30">
                  ✦ {user.email}
                </p>
              )}
            </motion.div>
          )}

          {state === "error" && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              data-testid="payment-success-error"
            >
              <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full border border-red-400/40 bg-red-400/10">
                <AlertCircle className="h-7 w-7 text-red-300" />
              </div>
              <h1 className="font-display text-3xl font-black tracking-tighter text-white md:text-4xl">
                {a.error_generic}
              </h1>
              <p className="mt-3 text-base text-white/55">
                {lang === "es" && "No hemos podido confirmar tu pago automáticamente. Si has pagado correctamente, escríbenos."}
                {lang === "en" && "We couldn't confirm your payment automatically. If you paid successfully, please contact us."}
                {lang === "sk" && "Nepodarilo sa nám automaticky potvrdiť tvoju platbu. Ak si zaplatil úspešne, napíš nám."}
              </p>
              <div className="mt-6">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-xs font-bold uppercase tracking-[0.25em] text-white/80 transition-all hover:border-white/40 hover:text-white"
                >
                  ← {lang === "en" ? "Back home" : lang === "sk" ? "Späť domov" : "Volver al inicio"}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
